/**
 * Business Process Event Logger — Frontend Utility
 *
 * Fire-and-forget: logEvent() never throws, never awaits, never shows errors to the user.
 * If the network is unavailable, the event is silently lost — this is intentional.
 *
 * session_id is a UUID generated once per page load and stored in this module.
 * It is NOT persisted to sessionStorage or localStorage.
 *
 * Usage:
 *   logEvent({
 *     event_id:    crypto.randomUUID(),
 *     event_type:  'estimate.saved',
 *     entity_type: 'estimate',
 *     entity_id:   estimateId,
 *     source:      'manual',
 *   });
 */

import { getTokens } from './authApi';

// ─── Session ID ───────────────────────────────────────────────────────────────
// Generated once per page load. Not persisted anywhere.
const SESSION_ID: string = (() => {
  try {
    return crypto.randomUUID();
  } catch {
    // Fallback for environments without crypto.randomUUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
})();

// ─── API base ─────────────────────────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001/api';

// ─── Event payload type ───────────────────────────────────────────────────────

export interface BusinessEventPayload {
  /** UUIDv4 generated by the caller for idempotency. */
  event_id: string;
  /** Must be in the 36-item allowlist on the server. Unknown types are silently dropped. */
  event_type: string;
  /** Entity domain. */
  entity_type: 'estimate' | 'work_order' | 'customer' | 'vehicle' | 'invoice' | 'ai';
  /** FK by convention. Optional for events that fire before an entity is persisted. */
  entity_id?: number;
  /** session_id is automatically injected — callers do not need to provide it. */
  session_id?: string;
  field_path?: string;
  /** JSON-serialized string. Must NOT contain customer PII. */
  old_value?: string;
  /** JSON-serialized string. Must NOT contain customer PII. */
  new_value?: string;
  source: 'manual' | 'ai_analyze' | 'ai_correct' | 'ai_interactive' | 'template' | 'system';
  ai_suggestion?: string;
  ai_was_overridden?: boolean;
  vehicle_year?: number;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_engine_size?: string;
  service_category?: string;
  service_description?: string;
}

// ─── logEvent ─────────────────────────────────────────────────────────────────

/**
 * Log a business process event.
 *
 * Fire-and-forget: this function returns void and swallows all errors.
 * The caller should NOT await this.
 */
export function logEvent(payload: BusinessEventPayload): void {
  // Guard: if no token, skip silently — no point logging unauthenticated events.
  const tokens = getTokens();
  if (!tokens?.accessToken) return;

  const body: BusinessEventPayload = {
    ...payload,
    session_id: payload.session_id ?? SESSION_ID,
  };

  // keepalive: true allows the request to complete even when the user navigates away.
  fetch(`${API_BASE_URL}/events/log`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokens.accessToken}`,
    },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // Intentionally silent — never surface errors to the UI.
  });
}

/**
 * Generate a UUID v4 for use as event_id.
 * Prefer crypto.randomUUID() which is available in all modern browsers.
 */
export function newEventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
